import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter, DialogDescription } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { Alert, AlertDescription, AlertTitle } from '../../../ui/alert';
import { Upload, FileText, CheckCircle2, Download, Info } from 'lucide-react';

const ImportEmployeesDialog = ({ open, onOpenChange }) => {
  const [stage, setStage] = useState('upload');

  const handleFile = () => {
    setStage('parsing');
    setTimeout(() => setStage('preview'), 900);
  };

  const commit = () => {
    setStage('done');
    setTimeout(() => {
      onOpenChange(false);
      setStage('upload');
    }, 1200);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-xl' onClose={() => onOpenChange(false)}>
        <DialogHeader>
          <DialogTitle>Import employees</DialogTitle>
          <DialogDescription>Upload a CSV with name, email, and office location.</DialogDescription>
        </DialogHeader>

        <DialogBody>
          {stage === 'upload' && (
            <>
              <label
                htmlFor='csv-file'
                className='relative block border-2 border-dashed border-input rounded-xl p-8 text-center cursor-pointer hover:border-primary transition-colors'>
                <input
                  id='csv-file'
                  type='file'
                  accept='.csv'
                  className='sr-only'
                  onChange={handleFile}
                />
                <div className='w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center text-accent mb-3'>
                  <Upload size={20} strokeWidth={1.5} />
                </div>
                <div className='font-medium mb-1'>Drop your CSV here, or click to browse</div>
                <div className='text-xs text-muted-foreground'>Up to 5,000 rows · max 2 MB</div>
              </label>

              <div className='mt-5 flex items-center justify-between text-xs text-muted-foreground'>
                <span>Need a template?</span>
                <Button variant='link' size='sm' className='h-auto p-0'>
                  <Download size={12} strokeWidth={1.5} />
                  Download CSV template
                </Button>
              </div>

              <Alert className='mt-5'>
                <Info size={14} strokeWidth={1.5} />
                <AlertTitle>Required columns</AlertTitle>
                <AlertDescription>
                  <code className='font-mono text-xs'>name, email, location</code>. The location must match one of your
                  campaign's office locations exactly.
                </AlertDescription>
              </Alert>
            </>
          )}

          {stage === 'parsing' && (
            <div className='flex flex-col items-center py-10 text-center'>
              <div className='w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-accent mb-4'>
                <FileText size={20} strokeWidth={1.5} />
              </div>
              <div className='font-medium mb-1'>Parsing employees.csv</div>
              <div className='text-xs text-muted-foreground'>Validating rows and matching locations...</div>
              <div className='w-48 h-1 bg-muted rounded-full mt-4 overflow-hidden'>
                <div className='h-full w-2/3 bg-primary animate-pulse' />
              </div>
            </div>
          )}

          {stage === 'preview' && (
            <>
              <div className='flex items-center gap-2 mb-3'>
                <FileText size={16} strokeWidth={1.5} className='text-accent' />
                <span className='text-sm font-medium'>employees.csv</span>
                <span className='text-xs text-muted-foreground'>· 392 rows</span>
              </div>

              <div className='grid grid-cols-3 gap-3 mb-4'>
                <div className='rounded-lg border border-border p-3'>
                  <div className='text-xs text-muted-foreground'>Total rows</div>
                  <div className='font-display text-2xl'>392</div>
                </div>
                <div className='rounded-lg border border-border p-3'>
                  <div className='text-xs text-success'>Valid</div>
                  <div className='font-display text-2xl text-success'>388</div>
                </div>
                <div className='rounded-lg border border-border p-3'>
                  <div className='text-xs text-warning'>Needs review</div>
                  <div className='font-display text-2xl text-warning'>4</div>
                </div>
              </div>

              <Alert>
                <Info size={14} strokeWidth={1.5} />
                <AlertTitle>4 rows flagged</AlertTitle>
                <AlertDescription>
                  3 rows have an unknown location (not in your campaign list) and 1 row has an invalid email. Resolve
                  before committing, or skip these rows.
                </AlertDescription>
              </Alert>
            </>
          )}

          {stage === 'done' && (
            <div className='flex flex-col items-center py-10 text-center'>
              <div className='w-12 h-12 rounded-full bg-success-bg flex items-center justify-center text-success mb-4'>
                <CheckCircle2 size={20} strokeWidth={1.5} />
              </div>
              <div className='font-medium mb-1'>388 employees imported</div>
              <div className='text-xs text-muted-foreground'>
                You can now send invitations from the dashboard.
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant='ghost' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {stage === 'preview' && <Button onClick={commit}>Import 388 valid rows</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportEmployeesDialog;
